import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';

import { coreApi } from '../../core/api';
import { type Session, User } from '../../core/user/users.types';
import { isLoggedInAtom } from './atoms';

type FetchUserForNativeAuthenticationProps = {
  secret: string;
  token: string;
};

const getUserForAuthentication = async () => {
  const { data } = await coreApi.get<{ user: User }>(`/users/me`);
  return data.user;
};

/**
 * Takes in a session secret and token to then exchange for a session from the API
 * This session is a child of the parent session and is used to authenticate requests to the API.
 * Will ignore current session cookies and only use the session secret and token provided.
 *
 * Returns the session object with the secure cookies set.
 *
 */
const getUserForNativeAuthentication = async ({
  secret,
  token,
}: FetchUserForNativeAuthenticationProps) => {
  const { data } = await coreApi.post<{ session: Session }>(
    `/users/by-headers`,
    {},
    {
      headers: {
        'Authentication-Session-Secret': secret,
        'Authentication-Session-Token': token,
      },
      withCredentials: true,
    }
  );
  return data.session;
};

const useFetchUserForAuthentication = () => {
  const [defaultStore] = useState(() => getDefaultStore());
  return useMutation({
    mutationFn: getUserForAuthentication,
    mutationKey: ['core', 'auth', 'user'],
    onSuccess: () => {
      defaultStore.set(isLoggedInAtom, true);
    },
    onError: () => {
      defaultStore.set(isLoggedInAtom, false);
    },
  });
};

/** Fetches the user session for native authentication */
const useFetchUserSessionForNativeAuthentication = () => {
  const [defaultStore] = useState(() => getDefaultStore());
  return useMutation({
    mutationFn: getUserForNativeAuthentication,
    mutationKey: ['core', 'auth', 'session'],
    onSuccess: () => {
      defaultStore.set(isLoggedInAtom, true);
    },
    onError: () => {
      defaultStore.set(isLoggedInAtom, false);
    },
  });
};

export { useFetchUserForAuthentication, useFetchUserSessionForNativeAuthentication };
